/**
 * LicenseHeaderChip & LicenseSyncEffect — License status indicators.
 * Extracted from App.js as part of AppRouter refactor.
 */
import React, { useEffect } from 'react';
import { useLicense } from '@/context/LicenseContext';

export const LicenseHeaderChip = ({ theme }) => {
  const { licensed, status, daysLeft, loaded, tier, openUpgradeModal, accessUnrestricted } = useLicense();
  const isDark = theme === 'dark';
  if (!loaded) return null;
  // Enterprise / unrestricted: never show warning chip
  if (accessUnrestricted) {
    return (
      <button data-testid="license-tier-chip" onClick={openUpgradeModal} title={`${tier} tier — click to manage`}
        style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:100,cursor:'pointer',fontSize:9,fontWeight:800,letterSpacing:'0.06em',textTransform:'uppercase',background:isDark?'rgba(59,130,246,0.12)':'#eff6ff',color:isDark?'#93c5fd':'#1d4ed8',border:`1px solid ${isDark?'#1e3a8a':'#bfdbfe'}`,flexShrink:0 }}>
        ★ {tier || 'Enterprise'}
      </button>
    );
  }
  const isExpired = !licensed || status === 'Expired';
  const isTrial = status === 'Trial';
  const showWarning = isExpired || isTrial || (status === 'Live' && daysLeft !== null && daysLeft <= 30);

  // Always render a tier chip (compact). If warning state, render the colored warning chip.
  if (!showWarning) {
    return (
      <button
        data-testid="license-tier-chip"
        onClick={openUpgradeModal}
        title={`${tier} tier — click to upgrade`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 8px', borderRadius: 100, cursor: 'pointer',
          fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
          background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
          color: isDark ? '#93c5fd' : '#1d4ed8',
          border: `1px solid ${isDark ? '#1e3a8a' : '#bfdbfe'}`,
          flexShrink: 0,
        }}
      >
        ★ {tier || 'Lite'}
      </button>
    );
  }
  return (
    <button
      data-testid="license-header-chip"
      onClick={openUpgradeModal}
      title={isExpired ? 'License expired — click to renew' : `License expiring in ${daysLeft} days — click to upgrade`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 100, cursor: 'pointer',
        fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
        background: isExpired ? (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2')
                              : (isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb'),
        color: isExpired ? (isDark ? '#fca5a5' : '#dc2626')
                        : (isDark ? '#fcd34d' : '#d97706'),
        border: `1px solid ${isExpired ? (isDark ? '#7f1d1d' : '#fecaca') : (isDark ? '#7c5500' : '#fde68a')}`,
        flexShrink: 0,
      }}
    >
      {isExpired ? '⚠ EXPIRED' : `⏳ ${daysLeft}d · ${tier || ''}`}
    </button>
  );
};

/** Triggers license check once globalContextData (client ID) is resolved after login */
export function LicenseSyncEffect({ globalContextData }) {
  const { refresh, loaded } = useLicense();
  useEffect(() => {
    if (!loaded && globalContextData) {
      // Try every known clientId field shape from the various login paths
      const clientId =
        globalContextData.clientId ||
        globalContextData.client_id ||
        globalContextData.company?.compId ||
        globalContextData.companyId ||
        '';
      if (clientId) refresh(clientId);
    }
  }, [globalContextData, loaded, refresh]);
  return null;
}
